import { optimisticSet } from '#lib/stores/optimistic.js';
import {
	apiNotificationSettingsUpdate,
	NotificationSound,
	type AppEvent,
	type UserNotificationSetting
} from '#lib/types/generated/index.js';
import { get, writable } from 'svelte/store';

type NotificationSettingStore = Record<string, UserNotificationSetting>;

export const notificationSettingStore = writable<NotificationSettingStore>({});

export function flipNotificationSetting(
	event: AppEvent | string,
	setting: keyof Omit<UserNotificationSetting, 'event' | 'sound' | 'capabilities'>
) {
	const current = get(notificationSettingStore);
	const targetSetting = current[event];
	const updated = {
		...current,
		[event]: {
			...targetSetting,
			[setting]: !targetSetting[setting]
		}
	};

	return optimisticSet(notificationSettingStore, updated, () =>
		apiNotificationSettingsUpdate({
			event: updated[event].event,
			notifyPushDesktop: updated[event].notifyPushDesktop,
			notifyPushMobile: updated[event].notifyPushMobile,
			notifyEmail: updated[event].notifyEmail,
			notifyApp: updated[event].notifyApp,
			sound: updated[event].sound
		})
	);
}

export function setNotificationSound(event: AppEvent | string, sound: NotificationSound) {
	const current = get(notificationSettingStore);
	const updated = {
		...current,
		[event]: {
			...current[event],
			sound
		}
	};

	return optimisticSet(notificationSettingStore, updated, () =>
		apiNotificationSettingsUpdate({
			event: updated[event].event,
			notifyPushDesktop: updated[event].notifyPushDesktop,
			notifyPushMobile: updated[event].notifyPushMobile,
			notifyEmail: updated[event].notifyEmail,
			notifyApp: updated[event].notifyApp,
			sound: updated[event].sound
		})
	);
}

export function getNotificationSound(event: AppEvent | string): string | undefined {
	const settings: Record<string, UserNotificationSetting | undefined> =
		get(notificationSettingStore);
	return settings[event]?.sound;
}

export function getAllNotificationEvents(): AppEvent[] {
	return Object.keys(get(notificationSettingStore)) as AppEvent[];
}
